/**
 * useLocalize / localize  — runtime auto-translation helper.
 *
 * Usage in any component:
 *   const { localize } = useLanguage();
 *   <h1>{localize({ ar: 'مرحبا', en: 'Hello' })}</h1>
 *
 * Behaviour:
 *   • If language === 'ar' or 'en' → returns the matching branch directly.
 *   • If language === 'es'/'hi'/'ur'/'fr'/'ru':
 *       1) Returns cached translation of opts.en if present.
 *       2) Otherwise enqueues a translation request, returns opts.en for now,
 *          and triggers a re-render once the translation arrives.
 *
 * The cache is persisted to localStorage (so subsequent visits are instant)
 * and to MongoDB on the backend (so the *first* visit by any user warms the
 * cache for everyone).
 */
const STORAGE_KEY = 'i18n_auto_cache_v1';
const FLUSH_DEBOUNCE_MS = 120;
const MAX_BATCH = 150;

const memCache = {};      // { lang: { en: 'translated' } }
const pending = new Set();// "lang:en" — queued strings awaiting flush
const queueByLang = {};   // { lang: Set<en> }
let flushTimer = null;
let backendUrl = '';
const subscribers = new Set();

const subscribe = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};
const notify = () => subscribers.forEach((cb) => cb());

const loadFromStorage = () => {
  if (Object.keys(memCache).length) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      Object.assign(memCache, parsed);
    }
  } catch { /* ignore */ }
};

const persistToStorage = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(memCache));
  } catch { /* quota or disabled — fine */ }
};

const flushQueue = async () => {
  flushTimer = null;
  const langs = Object.keys(queueByLang);
  for (const lang of langs) {
    const items = Array.from(queueByLang[lang] || []).slice(0, MAX_BATCH);
    if (!items.length) { delete queueByLang[lang]; continue; }
    queueByLang[lang] = new Set(Array.from(queueByLang[lang]).slice(MAX_BATCH));
    if (queueByLang[lang].size === 0) delete queueByLang[lang];

    try {
      const res = await fetch(`${backendUrl}/api/i18n/auto-translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strings: items, target: lang, source: 'en' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      memCache[lang] = memCache[lang] || {};
      Object.assign(memCache[lang], data.translations || {});
      items.forEach((s) => pending.delete(`${lang}:${s}`));
      persistToStorage();
      notify();
    } catch {
      // On failure: drop the queued items so the UI just keeps showing EN.
      items.forEach((s) => pending.delete(`${lang}:${s}`));
    }

    // If queue still has items for this lang, schedule another flush.
    if (queueByLang[lang] && queueByLang[lang].size) {
      scheduleFlush();
    }
  }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(flushQueue, FLUSH_DEBOUNCE_MS);
};

const enqueue = (lang, text) => {
  const key = `${lang}:${text}`;
  if (pending.has(key)) return;
  pending.add(key);
  queueByLang[lang] = queueByLang[lang] || new Set();
  queueByLang[lang].add(text);
  scheduleFlush();
};

/**
 * Make a localiser bound to a specific language. Returns a pure function
 * `localize(opts)` that components call.
 */
export function makeLocalize(language, apiBaseUrl) {
  loadFromStorage();
  if (apiBaseUrl) backendUrl = apiBaseUrl;

  return (opts) => {
    if (!opts || typeof opts !== 'object') return opts ?? '';
    // Direct hit
    if (Object.prototype.hasOwnProperty.call(opts, language) && opts[language] != null) {
      return opts[language];
    }
    // ar / en need no auto-translate — they're authored in source.
    if (language === 'ar') return opts.ar ?? opts.en ?? '';
    if (language === 'en') return opts.en ?? opts.ar ?? '';

    // Other languages → consult cache, else queue translation
    const en = opts.en;
    if (!en) return opts.ar || '';
    const langCache = memCache[language] || {};
    if (langCache[en]) return langCache[en];

    enqueue(language, en);
    return en;  // immediate fallback while translation is in flight
  };
}

/**
 * Subscribe to cache updates so React components can re-render when fresh
 * translations land. Returns an unsubscribe function.
 */
export function onLocalizeUpdate(cb) {
  return subscribe(cb);
}

/**
 * Test/reset hook used by Storybook & unit tests only.
 */
export function _resetLocalizeCacheForTests() {
  Object.keys(memCache).forEach((k) => delete memCache[k]);
  pending.clear();
  Object.keys(queueByLang).forEach((k) => delete queueByLang[k]);
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
}
